import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarRating } from './StarRating';
import { PromoCTAButton } from './PromoCTAButton';
import type { PropFirm } from '../types';

function StickyBar({ firm }: { firm: PropFirm }) {
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyBar(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const discount = firm.evaluation?.discount;
  const originalPrice = firm.evaluation?.fee;
  const discountedPrice = discount?.discountedPrice;
  const savingsAmount =
    originalPrice && discountedPrice ? (originalPrice - discountedPrice).toFixed(2) : null;
  const savingsPercent =
    savingsAmount && originalPrice
      ? Math.round((Number(savingsAmount) / originalPrice) * 100)
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: showStickyBar ? 1 : 0, y: showStickyBar ? 0 : -20 }}
      transition={{ duration: 0.3 }}
      className="fixed top-[64px] left-0 right-0 z-50"
    >
      <div className="bg-gradient-to-r from-[#111] via-[#111]/90 to-[#111] border-b border-white/10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="bg-white/5 rounded-xl border border-white/10 px-4 py-3 flex flex-col md:flex-row items-center justify-between gap-4 text-yellow-100">

            {/* Left: Logo + Name + Rating */}
            <div className="flex items-center gap-3">
              <img
                src={`/assets/logos/${firm.id}/logo.svg`}
                alt={firm.name}
                className="h-12 w-auto"
              />
              <div className="hidden md:block font-bold text-yellow-100 text-lg">
                {firm.name}
              </div>
              <div className="flex items-center gap-1 ml-4">
                <StarRating rating={firm.rating} />
                <span className="ml-1 text-sm text-yellow-200 flex items-center gap-1">
                  {firm.reviewCount?.toLocaleString() || 0} reviews
                  <img src="/assets/trustpilot-logo.svg" alt="Trustpilot" className="h-4" />
                </span>
              </div>
            </div>

            {/* Right: Price Info + CTA */}
            <div className="flex flex-col items-center gap-1 md:items-end md:gap-2">

              {discountedPrice && (
                <div className="flex flex-wrap items-center justify-center gap-2 text-sm text-yellow-300">
                  <span className="line-through text-yellow-500/70">${originalPrice.toFixed(2)}</span>
                  <span className="font-bold text-yellow-400">${discountedPrice.toFixed(2)}</span>
                  {savingsAmount && (
                    <span className="bg-green-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                      Save ${savingsAmount} {savingsPercent ? `(${savingsPercent}%)` : ''}
                    </span>
                  )}
                </div>
              )}

              {!discountedPrice && (
                <div className="font-semibold text-yellow-400 text-sm">
                  Get Funded Today
                </div>
              )}

              <PromoCTAButton
                firmId={firm.id}
                code={discount?.code}
                percent={discount?.percent}
                validUntilRemark={discount?.validUntilRemark}
                firstPurchaseOnly={discount?.firstPurchaseOnly}
                className="w-full md:w-[180px]"
              />

              {discount?.validUntil && (
                <div className="text-xs text-yellow-300 animate-pulse">
                  ⏳ Offer ends soon
                </div>
              )}

              <div className="text-xs text-yellow-400 mt-1">
                Trusted by 24,000+ Traders | Instant Signup
              </div>
            </div>

          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default StickyBar;
